import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import {Cell, Pie, PieChart, Legend, ResponsiveContainer, Label, Text} from 'recharts';
import {FormattedMessage, useIntl} from 'react-intl';
import {useBreakpoints} from '../BreakpointContext/index.mjs';


const DonutChart = ({className, data, colors, centerLabel, header, legendProps}) => {
  const bemClass = 'chart';
  const {xl} = useBreakpoints();
  const classes = classnames(bemClass, `${bemClass}--donut`, className);
  const intl = useIntl();

  const RADIAN = Math.PI / 180;

  const renderCustomizedLabel = ({cx, cy, midAngle, innerRadius, outerRadius, percent}) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      percent >= 0.065 && (
        <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central">
          {`${(percent * 100).toFixed(1)}%`}
        </text>
      ) || null
    );
  };

  const renderCenterLabel = props => {
    const {
      viewBox: {cx, cy, innerRadius},
    } = props;
    const staticValue = innerRadius * 1.5;
    const staticHeight = innerRadius * 0.5;
    const rectPositioningProps = {
      x: cx - staticValue / 2,
      y: cy - staticHeight / 2,
    };
    const positioningProps = {
      x: cx,
      y: cy,
      textAnchor: 'middle',
      verticalAnchor: 'middle',
    };

    return (
      <g>
        <rect
          {...rectPositioningProps}
          width={`${staticValue}px`}
          height={`${staticHeight}px`}
          className="label__rect"
        />
        <Text {...positioningProps} className="label__text" scaleToFit>
          {centerLabel}
        </Text>
      </g>
    );
  };

  return (
    <div className={classes}>
      {
        header && (
          <h3 className="chart__header">
            <FormattedMessage id={header} />
          </h3>
        )
      }
      <ResponsiveContainer className="chart__responsive" width="100%" height="100%" minHeight="312px" debounce={1000}>
        <PieChart className="chart__wrapper" margin={{top: 0, right: 0, left: 0, bottom: 0}}>
          <Pie
            isAnimationActive={false}
            className="chart__layer"
            data={data}
            dataKey="value"
            nameKey="appKey"
            innerRadius="50%"
            outerRadius="100%"
            legendType="circle"
            labelLine={false}
            label={renderCustomizedLabel}
          >
            {centerLabel && <Label position="center" content={renderCenterLabel} />}
            {data.length &&
              data.map((entry, index) => {
                return <Cell key={`cell-${index}`} stroke="transparent" fill={colors[entry.appkey] || '#ff0000'} />;
              })}
          </Pie>
          <Legend
            className="chart__legend"
            margin={{top: 20}}
            align={xl ? 'right' : 'left'}
            {...(xl && {verticalAlign: 'middle', layout: 'vertical'})}
            payload={data.map(item => ({
              id: item.appkey,
              type: 'circle',
              color: colors[item.appkey],
              value: `${intl.formatMessage({id: item.appkey})}`,
            }))}
            {...legendProps}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

DonutChart.propTypes = {
  className: PropTypes.string,
  header: PropTypes.string,
  data: PropTypes.array,
  colors: PropTypes.object,
  centerLabel: PropTypes.string,
  legendProps: PropTypes.object,
  viewBox: PropTypes.object,
};

export default DonutChart;
